/* Ruby Rose — background video loader.
   The <video> ships with no source. We only attach one when the connection and
   the user's preferences allow it, so slow links and data-saver mode keep the
   poster image and download nothing. */

class RubyVideo extends HTMLElement {
  connectedCallback() {
    this.init();

    // Guard against upgrading before children are parsed (see ruby-carousel.js).
    if (!this.initialised) {
      document.addEventListener("DOMContentLoaded", () => this.init(), { once: true });
    }
  }

  init() {
    if (this.initialised) return;

    this.video = this.querySelector("video");
    if (!this.video) return;
    this.initialised = true;

    if (!this.shouldLoad()) {
      this.setAttribute("data-rr-poster-only", "");
      return;
    }

    // Defer until the section is near the viewport.
    if ("IntersectionObserver" in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.load();
            this.observer.disconnect();
          }
        },
        { rootMargin: "200px" }
      );
      this.observer.observe(this);
    } else {
      this.load();
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  shouldLoad() {
    const connection =
      navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    // Explicit data-saver, or a connection slow enough that a background video
    // is a poor trade.
    if (connection) {
      if (connection.saveData === true) return false;
      if (/(^|-)2g$/.test(connection.effectiveType || "")) return false;
    }

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;

    if (
      this.hasAttribute("data-rr-desktop-only") &&
      window.matchMedia("(max-width: 767px)").matches
    ) {
      return false;
    }

    return true;
  }

  load() {
    const sources = [...this.querySelectorAll("source[data-src]")];
    if (!sources.length) return;

    // Only the data-src is promoted. Deliberately NOT setting video.src: the
    // <source> list is ordered HLS-then-mp4 so each browser takes the format it
    // can decode, and a src on the element would override the whole list.
    sources.forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute("data-src");
    });

    this.video.load();
    this.keepPlaying();
    this.attempt(true);
  }

  /* Mobile browsers pause background video for reasons the page never asked
     for — tab switch, an incoming call, bfcache restore, Low Power Mode — and
     do not always resume. Nothing here forces playback against a refusal: a
     rejected play() leaves the video paused and fires no further pause event,
     so the retries end there. */
  keepPlaying() {
    if (this.watching) return;
    this.watching = true;
    this.retries = 0;

    this.video.addEventListener("playing", () => {
      this.retries = 0;
      this.removeAttribute("data-rr-poster-only");
    });

    this.video.addEventListener("pause", () => {
      // A pause while the page is hidden is the browser saving power.
      // visibilitychange resumes it when the page comes back.
      if (!document.hidden) setTimeout(() => this.attempt(), 150);
    });

    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) this.attempt();
    });

    window.addEventListener("pageshow", () => this.attempt());
  }

  attempt(first = false) {
    if (!first) {
      if (!this.video.paused || document.hidden) return;
      // Bounded so a browser that pauses on every play cannot spin.
      if (this.retries >= 10) return;
      this.retries += 1;
    }

    const started = this.video.play();
    if (!started || typeof started.catch !== "function") return;

    started.catch(() => {
      // Autoplay refused outright (iOS Low Power Mode is the common case).
      // The poster is the intended fallback; a real tap can still start it.
      this.setAttribute("data-rr-poster-only", "");
      if (first) this.awaitGesture();
    });
  }

  awaitGesture() {
    const start = () => this.video.play().catch(() => {});
    ["touchstart", "pointerdown", "keydown"].forEach((type) => {
      window.addEventListener(type, start, { once: true, passive: true });
    });
  }
}

if (!customElements.get("ruby-video")) {
  customElements.define("ruby-video", RubyVideo);
}
